import React, { useState, useEffect, useContext } from 'react';
import { getOrgContractsByOrgId } from 'api/SupraController';
import { message } from '@brokerbay/design-components';
import {
  SupraSystemSubscriptionStatus,
  SupraSystemSubscriptionType,
} from '@bb/domain/dist/types';
import Axios from 'axios';
import { AuthContext } from 'contexts';
import { getPriceMap } from 'api/get';
import { UserSubRoles } from '@bb/users';
import { SUBSCRIPTION_UPDATED_EVENT } from '../BalanceCard';
import FormContext from '../../../UserViewPage/FormContext';

export type CanManageType = {
  response: boolean;
  message?: string;
};

export type SupraSystemSubscriptionState = {
  id?: string;
  type: SupraSystemSubscriptionType;
  subRole?: UserSubRoles | '';
  status?: SupraSystemSubscriptionStatus;
  price?: number;
  billingFrequency?: string;
};

export type AnsweringServiceSubscriptionState = {
  id?: string;
  type: 'answering' | 'default';
  serviceType?: 'LiveConciergePlus' | 'AppointmentDesk';
  coverageType?: '24/7' | 'afterHours';
  status?: 'launched' | 'prelaunch' | 'onHold';
  price?: number;
  billingFrequency?: string;
};

const DEFAULT_BROKERAGE_SUBSCRIPTION: AnsweringServiceSubscriptionState = {
  type: 'default',
  id: 'brokerbayShowingSystem',
};

enum subscriptionTypes {
  answeringServiceSubscription,
  supraSystemSubscription,
}

type ContextShape = {
  subscriptions: (
    | AnsweringServiceSubscriptionState
    | SupraSystemSubscriptionState
  )[];
  loadingSubscription: boolean;
  cancellingSubscription: boolean;
  cancelSubscription: (subscriptionId: string) => Promise<void>;
  showPlanManage: boolean;
  orgPlanPrice: number;
  downgradeBrokeragePlan: () => Promise<void>;
  canManageSubscription: CanManageType;
};

const defaultContext = {} as ContextShape;

export const SubscriptionsCardContext =
  React.createContext<ContextShape>(defaultContext);

export interface SubscriptionsCardContextProps {
  answeringSubscriptionLevel: 'org' | 'user';
  userId?: string;
  redirectToMain?: () => Promise<void>;
  canManageSubscription: CanManageType;
}

const SubscriptionsCardContextProvider: React.FC<
  SubscriptionsCardContextProps
> = ({
  answeringSubscriptionLevel,
  userId,
  redirectToMain,
  children,
  canManageSubscription,
}) => {
  const { userState } = useContext(FormContext);
  const userSupraSub = userState?.supraSubscription;

  // Start the list of subscriptions empty, or with the DEFAULT_BROKERAGE_SUBSCRIPTION
  const [subscriptions, setSubscription] = useState<
    (AnsweringServiceSubscriptionState | SupraSystemSubscriptionState)[]
  >(
    answeringSubscriptionLevel === 'org'
      ? [DEFAULT_BROKERAGE_SUBSCRIPTION]
      : [],
  );
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);
  const [showPlanManage, setShowPlanManage] = useState(false);
  const [orgPlanPrice, setOrgPlanPrice] = useState(0);
  const { orgPlan, onboardingPortalConfig } = useContext(AuthContext);

  // Fetch the org price that we might display on the ManagePlanModal
  const fetchOrgPlanPrice = async () => {
    const { monthlyPrice } = await getPriceMap();
    setOrgPlanPrice(monthlyPrice);
  };

  // Each time a subscription finishes loading, append it to the list of finished ones.
  const [loadedSubscriptions, setLoadedSubscriptions] = useState<
    subscriptionTypes[]
  >([]);
  const updateLoadedDescriptions = (
    finishedSubscription?: subscriptionTypes,
  ) => {
    if (
      finishedSubscription !== undefined &&
      !loadedSubscriptions.includes(finishedSubscription)
    ) {
      setLoadedSubscriptions(existing => [...existing, finishedSubscription]);
    }
  };

  /**
   * The method to fetch Answering Service Subs and add them to the subscriptions array.
   */
  const fetchAnsweringServiceSubscriptions = async () => {
    // Pass in the userId if this is a user subscription not org
    const params = {
      subscriptionLevel: answeringSubscriptionLevel,
      ...(answeringSubscriptionLevel === 'user' && { userId }),
    };
    // Go get the subscription from the db.
    try {
      const { data: answeringServiceSub } =
        await Axios.get<AnsweringServiceSubscriptionState | null>(
          '/answering_subscription/find_own',
          { params },
        );
      if (answeringServiceSub) {
        // Set the price & billing frequency to what we will display
        if (answeringServiceSub.price) {
          answeringServiceSub.price /= 100;
          answeringServiceSub.billingFrequency =
            answeringSubscriptionLevel === 'org' ? 'listing / month' : 'month';
        }
        // Add the one or two subscriptions to the list
        const subscriptionList: AnsweringServiceSubscriptionState[] = [
          { ...answeringServiceSub, type: 'answering' },
        ];
        if (answeringSubscriptionLevel === 'org') {
          subscriptionList.push(DEFAULT_BROKERAGE_SUBSCRIPTION);
        }
        setSubscription(existing => [...existing, ...subscriptionList]);
      }
    } catch (err) {
      message.error('Cannot fetch list of answering subscriptions.'); // TODO: Discover why this is throwing. Should it just be silent because null is returned for no contracts?
    }
    updateLoadedDescriptions(subscriptionTypes.answeringServiceSubscription);
  };

  /**
   * The method to fetch Supra Sub and add them to the subscriptions array.
   */
  const fetchSupraSystemSubscription = async () => {
    // If this current user has an active subscription with a billingProductName,
    // add that sub to our list of subscriptions.
    if (
      userSupraSub &&
      userSupraSub.status === 'subscribed' &&
      userSupraSub.type &&
      userSupraSub.billingProductName
    ) {
      const userSupraSubscription: SupraSystemSubscriptionState = {
        id: userSupraSub.id,
        type: userSupraSub.type,
        subRole: userState.subRole,
        status: userSupraSub.status,
      };
      // Grab the contract that the MLS this user is under has for the user's Supra
      // subscription product, and grab the rate and frequency they've agreed to pay
      const contracts = await getOrgContractsByOrgId({
        orgId: userSupraSub.orgId,
      });
      const subscriptionContract = contracts.find(
        singleContract =>
          singleContract.contractRateLabel === userSupraSub.billingProductName,
      );
      if (subscriptionContract?.rate) {
        const payRate = subscriptionContract?.rate;
        userSupraSubscription.price = parseFloat(payRate.replace('$', ''));

        // Use either productSubscriptionCycle or profileBillingCycle to get the frequency.
        let frequency = '';
        if (subscriptionContract.productSubscriptionCycle) {
          frequency = subscriptionContract.productSubscriptionCycle;
        } else if (subscriptionContract.profileBillingCycle) {
          frequency = subscriptionContract.profileBillingCycle;
        }
        switch (frequency) {
          case 'YEARLY':
            userSupraSubscription.billingFrequency = 'year';
            break;
          case 'MONTHLY':
            userSupraSubscription.billingFrequency = 'month';
            break;
          case 'QUARTERLY':
            userSupraSubscription.billingFrequency = 'quarter';
            break;
          default:
            userSupraSubscription.billingFrequency = '';
            console.warn(
              "Could not find a valid billing frequency for the user's Supra Subscription. Displaying a blank value to the user.",
            );
        }
      }
      setSubscription(existing => [...existing, userSupraSubscription]);
    }
    updateLoadedDescriptions(subscriptionTypes.supraSystemSubscription);
  };

  // Run the initializing code when this mounts.
  useEffect(() => {
    fetchAnsweringServiceSubscriptions();
    fetchSupraSystemSubscription();
    // If this is a subscription being paid for by the agent (not the brokerage)
    // get the price of the plan.
    if (onboardingPortalConfig.plan === 'AGENT') fetchOrgPlanPrice();
  }, []);

  // If this is a subscription being paid for by the brokerage (not the agent)
  // and they had a previous plan, show the plan management UI.
  useEffect(() => {
    if (orgPlan) {
      const { name, previousPlanName } = orgPlan!;
      const planManage = name === 'BROKERAGE' && Boolean(previousPlanName);
      setShowPlanManage(planManage);
    }
  }, [orgPlan]);

  // Trigger a re-render on the page when all subscriptions are loaded.
  useEffect(() => {
    if (
      loadedSubscriptions.includes(
        subscriptionTypes.answeringServiceSubscription,
      ) &&
      loadedSubscriptions.includes(subscriptionTypes.supraSystemSubscription)
    ) {
      setLoadingSubscription(false);
    }
  }, [loadedSubscriptions]);

  /**
   * The method to call the endpoint of actually cancelling the Answering Service Subscription.
   * @param subscriptionId
   */
  const cancelSubscription = async (subscriptionId: string) => {
    try {
      setCancellingSubscription(true);
      await Axios.post(`/answering_subscription/${subscriptionId}`);
      const params = {
        subscriptionLevel: answeringSubscriptionLevel,
        ...(answeringSubscriptionLevel === 'user' && { userId }),
      };
      // We need to retrieve the subscriptions in case an there was a downgrade (i.e. LiveConcierge to AppointmentDesk)
      const { data } =
        await Axios.get<AnsweringServiceSubscriptionState | null>(
          '/answering_subscription/find_own',
          {
            params,
          },
        );
      if (data) {
        const subscriptionList: AnsweringServiceSubscriptionState[] = [
          { ...data, type: 'answering' },
        ];
        if (answeringSubscriptionLevel === 'org') {
          subscriptionList.push(DEFAULT_BROKERAGE_SUBSCRIPTION);
        }
        setSubscription(subscriptionList);
        fetchSupraSystemSubscription();
      } else {
        const newSubscriptions = subscriptions.filter(
          ({ id }) => id !== subscriptionId,
        );
        setSubscription(newSubscriptions);
        fetchSupraSystemSubscription();
      }
      // Send a signal that the subscription may have been updated so other pages can dynamically re-render content
      document.dispatchEvent(new CustomEvent(SUBSCRIPTION_UPDATED_EVENT));
    } catch (err) {
      message.error('Cannot cancel this subscription.');
    }
    setCancellingSubscription(false);
  };

  // The method call to downgrade the Answering Service Subscription to the Agent plan.
  const downgradeBrokeragePlan = async () => {
    try {
      await Axios.post(`/plan/cancel`);
      message.success("You've successfully downgraded to the Agent Plan");
      if (redirectToMain) await redirectToMain();
    } catch (err) {
      message.error('Cannot downgrade to Agent Plan.');
    }
  };

  return (
    <SubscriptionsCardContext.Provider
      value={{
        subscriptions,
        loadingSubscription,
        cancellingSubscription,
        cancelSubscription,
        showPlanManage,
        orgPlanPrice,
        downgradeBrokeragePlan,
        canManageSubscription,
      }}
    >
      {children}
    </SubscriptionsCardContext.Provider>
  );
};

export default SubscriptionsCardContextProvider;
